import React, { useEffect, useState, useCallback } from 'react';
import { ChevronLeft, Navigation, PauseCircle, ParkingCircle } from 'lucide-react';
import TripSegmentMap, { TripSegment } from '../components/Map/TripSegmentMap';
import api from '../api/client';
import { normalizeUnit, kmToUnit, formatDistanceKm, speedUnitLabel } from '../utils/units';
import './History.css';

interface Device {
  id: string;
  name: string;
  preferred_unit?: string;
}

const TYPE_LABEL: Record<TripSegment['type'], string> = {
  trasa: 'Trasa',
  zatrzymanie: 'Zatrzymanie',
  postoj: 'Postój',
};

function fmtTime(iso: string): string {
  return new Date(iso + 'Z').toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
}

function fmtDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  if (h > 0) return `${h} godz. ${m} min`;
  return `${m} min`;
}

function SegmentIcon({ type }: { type: TripSegment['type'] }) {
  if (type === 'trasa') return <Navigation size={18} color="#e63946" />;
  if (type === 'zatrzymanie') return <PauseCircle size={18} color="#ffb703" />;
  return <ParkingCircle size={18} color="#4cc9f0" />;
}

export default function History() {
  const [devices, setDevices] = useState<Device[]>([]);
  const [selectedDevice, setSelectedDevice] = useState('');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [segments, setSegments] = useState<TripSegment[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<TripSegment | null>(null);
  const unit = normalizeUnit(devices.find((d) => d.id === selectedDevice)?.preferred_unit);

  useEffect(() => {
    api.get('/devices/').then((res) => {
      setDevices(res.data);
      if (res.data.length > 0) setSelectedDevice(res.data[0].id);
    });
  }, []);

  const load = useCallback(() => {
    if (!selectedDevice) return;
    setLoading(true);
    setSelected(null);
    api.get(`/devices/${selectedDevice}/trips`, { params: { date } })
      .then((res) => setSegments(res.data))
      .catch(() => setSegments([]))
      .finally(() => setLoading(false));
  }, [selectedDevice, date]);

  useEffect(() => { load(); }, [load]);

  // Detail view: selected segment on the map, full height.
  if (selected) {
    return (
      <div className="history-page">
        <div className="history-detail-header">
          <button className="history-back" onClick={() => setSelected(null)}>
            <ChevronLeft size={20} />
            <span>Lista</span>
          </button>
          <div className="history-detail-title">
            <SegmentIcon type={selected.type} />
            <span>{TYPE_LABEL[selected.type]}</span>
            <span className="history-detail-time">{fmtTime(selected.start_time)}–{fmtTime(selected.end_time)}</span>
          </div>
        </div>
        <div className="history-detail-stats">
          <span>{fmtDuration(selected.duration_seconds)}</span>
          {selected.type === 'trasa' && (
            <>
              <span>{formatDistanceKm(selected.distance_m / 1000, unit, 1)}</span>
              <span>śr. {Math.round(kmToUnit(selected.avg_speed_kmh, unit))} {speedUnitLabel(unit)}</span>
              {selected.max_speed_kmh != null && <span>maks. {Math.round(kmToUnit(selected.max_speed_kmh, unit))} {speedUnitLabel(unit)}</span>}
            </>
          )}
        </div>
        <div className="history-map">
          <TripSegmentMap segment={selected} unit={unit} />
        </div>
      </div>
    );
  }

  return (
    <div className="history-page">
      <div className="history-controls">
        {devices.length > 1 && (
          <select value={selectedDevice} onChange={(e) => setSelectedDevice(e.target.value)}>
            {devices.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
        )}
        <input
          type="date"
          value={date}
          max={new Date().toISOString().slice(0, 10)}
          onChange={(e) => setDate(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="history-empty"><div className="spinner" /></div>
      ) : segments.length === 0 ? (
        <div className="history-empty"><p>Brak tras dla wybranego dnia</p></div>
      ) : (
        <ul className="history-list">
          {segments.map((s, i) => (
            <li key={i} className={`history-item history-item--${s.type}`} onClick={() => setSelected(s)}>
              <span className="history-item-icon"><SegmentIcon type={s.type} /></span>
              <div className="history-item-content">
                <div className="history-item-top">
                  <span className="history-item-type">{TYPE_LABEL[s.type]}</span>
                  <span className="history-item-time">{fmtTime(s.start_time)}–{fmtTime(s.end_time)}</span>
                </div>
                <div className="history-item-stats">
                  <span>{fmtDuration(s.duration_seconds)}</span>
                  {s.type === 'trasa' && (
                    <>
                      <span>{formatDistanceKm(s.distance_m / 1000, unit, 1)}</span>
                      <span>śr. {Math.round(kmToUnit(s.avg_speed_kmh, unit))} {speedUnitLabel(unit)}</span>
                    </>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
